import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { axiosInstance } from '../../context/AuthContext';
import { useSavedArtworks } from '../../context/SavedArtworksContext';
import { useAuthContext } from '../../hooks/useAuthContext';

const ArtworkCard = ({
  _id,
  id,
  slug,
  title,
  artist,
  image,
  views: initialViews,
  price,
  isVerified,
}) => {
  const { user } = useAuthContext();
  const { isArtworkSaved, toggleSaveArtwork } = useSavedArtworks();
  const artworkId = _id || id;
  const isSaved = isArtworkSaved(artworkId);
  const [views, setViews] = React.useState(initialViews || 0);

  const detailPath = slug
    ? `/artozphere/artwork/${slug}`
    : `/artozphere/artwork/${artworkId}`;

  React.useEffect(() => {
    if (!artworkId) return;

    axiosInstance
      .put(`/artworks/${artworkId}/view`)
      .then((res) => {
        if (res.data?.views !== undefined) setViews(res.data.views);
      })
      .catch(() => {});

    const interval = setInterval(async () => {
      try {
        const res = await axiosInstance.get(`/artworks/${artworkId}/views`);
        if (res.data?.views !== undefined) setViews(res.data.views);
      } catch {
        /* ignore */
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [artworkId]);

  const handleSave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return;

    toggleSaveArtwork({
      id: artworkId,
      _id: artworkId,
      title,
      artist:
        typeof artist === 'object'
          ? artist.username || `${artist.firstName || ''} ${artist.lastName || ''}`
          : artist,
      image,
      views,
      price,
      isVerified,
    });
  };

  const artistName =
    typeof artist === 'object'
      ? artist?.username || `${artist?.firstName || ''} ${artist?.lastName || ''}`
      : artist;

  return (
    <div className="w-full max-w-80 sm:max-w-xs md:max-w-sm lg:max-w-80 mx-auto bg-white rounded-2xl shadow-xl drop-shadow overflow-hidden hover:shadow-2xl transition-shadow duration-300">
      <div className="p-3 sm:p-4 md:p-5">
        <div className="flex items-start sm:items-center justify-between mb-1">
          <div>
            <div className="flex items-center gap-1 sm:gap-2">
              <Link
                to={detailPath}
                className="font-semibold text-base sm:text-lg font-sans hover:text-blue-600 transition-colors"
              >
                {title}
              </Link>
              
            </div>
            <p className="text-xs sm:text-sm text-gray-500 mb-1 sm:mb-2 ml-1 flex items-center gap-1">
              by {artistName}
              {isVerified && (
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  title="Verified Artist"
                  className="w-3 h-3 sm:w-4 sm:h-4 text-blue-500"
                />
              )}
            </p>
          </div>
          <button onClick={handleSave} className="transition-colors duration-200" type="button">
            <FontAwesomeIcon
              icon={faHeart}
              className={`w-5 h-5 sm:w-6 sm:h-6 ${
                isSaved ? 'text-red-500' : 'text-gray-300 hover:text-gray-400'
              }`}
            />
          </button>
        </div>

        <div className="relative">
          <Link to={detailPath}>
            <img
              src={image}
              alt={`${title} artwork`}
              className="w-full h-48 sm:h-56 md:h-64 object-cover rounded-lg cursor-pointer hover:opacity-95 transition-opacity"
            />
          </Link>
        </div>

        <div className="flex justify-between text-xs sm:text-sm font-sans backdrop-blur-md bg-slate-800/60 border border-gray-300/30 items-center py-3 sm:py-4 px-3 sm:px-4 rounded-b-lg -mt-2 shadow-lg">
          <div className="flex flex-col items-center text-white mb-2">
            <span className="font-bold text-sm sm:text-base">{views}</span>
            <span className="text-rose-300 text-[9px] sm:text-xs">current views</span>
          </div>
          <div className="flex flex-col items-center text-white mb-2">
            <span className="font-bold text-sm sm:text-base">{price}</span>
            <span className="text-emerald-300 text-[9px] sm:text-xs">current price</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtworkCard;
